/**
 * WishlistSprite — resolves the correct colour art for a Wish List entry.
 *
 * A wished copy is a `(variant, dexNumber, formId?)` triple, so the sprite has
 * to honour all three axes: a shiny-family variant uses the shiny art, a
 * specific form (e.g. `vivillon-meadow`) uses that form's sprite, and a
 * Mega/G-Max variant uses the form art. Everything else falls back to the
 * base-species art via CaughtSprite's own candidate chain.
 *
 * Shared by the Wish List screen slots and the Compare-page wish rows so both
 * render identically.
 */
import { CaughtSprite } from "./CaughtSprite";
import { FORM_GROUPS_BY_SPECIES_ID } from "@/lib/generated/pokemonForms.generated";
import { speciesFormArtCandidates } from "@/lib/formSpriteCandidates";
import {
  isMegaGmaxVariant,
  megaGmaxSlug,
  megaGmaxSpriteCandidates,
} from "@/lib/megaGmaxSprites";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import type { WishlistEntry } from "@/lib/wishlistStore";

// Variants rendered with shiny art (mirrors FormGrid / Pokedex conventions).
const SHINY_FAMILY = new Set<PokedexVariant>(["shiny", "shundo", "shinyLucky"]);

/** The trainer-facing form name for an entry, or null for the base species. */
export function wishlistFormLabel(entry: WishlistEntry): string | null {
  if (!entry.formId) return null;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(entry.dexNumber);
  const form = group?.forms.find((f) => f.formId === entry.formId);
  return form?.displayName ?? null;
}

export function WishlistSprite({
  entry,
  size,
  fill = false,
  ariaLabel,
}: {
  entry: WishlistEntry;
  size?: number;
  fill?: boolean;
  ariaLabel?: string;
}) {
  const shiny = SHINY_FAMILY.has(entry.variant);
  let candidates: string[] | undefined;
  let formSlug: string | undefined;

  if (entry.formId) {
    candidates = speciesFormArtCandidates(entry.dexNumber, entry.formId, { shiny });
  } else if (isMegaGmaxVariant(entry.variant)) {
    candidates = megaGmaxSpriteCandidates(entry.variant, entry.dexNumber);
    formSlug = megaGmaxSlug(entry.variant, entry.dexNumber) ?? undefined;
  }

  return (
    <CaughtSprite
      id={entry.dexNumber}
      shiny={shiny}
      fill={fill}
      size={size}
      formSpriteCandidates={candidates}
      formSlug={formSlug}
      ariaLabel={ariaLabel}
    />
  );
}
